import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ruler, Sparkles, Star, Crown } from "lucide-react";
import { FINISH_LEVELS, WizardFormData } from "@/types/wizard";

interface Step6SizeMaterialsProps {
  formData: WizardFormData;
  updateFormData: (updates: Partial<WizardFormData>) => void;
  isDark: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  standard: <Sparkles className="w-8 h-8" />,
  elite: <Star className="w-8 h-8" />,
  deluxe: <Crown className="w-8 h-8" />,
};

const Step6SizeMaterials = ({ formData, updateFormData, isDark }: Step6SizeMaterialsProps) => {
  // Check if user has permit or is working with architect AND should enter square footage
  const showSquareFootageOnly = 
    formData.designChoice === "have_permit" || 
    formData.designChoice === "working_with_architect";

  const inputStyles = isDark
    ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414] rounded-xl text-xl py-6"
    : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#d1d1d1,inset_-4px_-4px_8px_#ffffff] rounded-xl text-xl py-6";

  const labelStyles = isDark ? "text-white" : "text-gray-900";

  const getCardStyles = (isSelected: boolean) => {
    if (isSelected) {
      return {
        backgroundColor: "#E4683B",
        color: "#ffffff",
        boxShadow: isDark
          ? "4px 4px 8px #000000, -4px -4px 8px #141414"
          : "4px 4px 8px #d1d1d1, -4px -4px 8px #ffffff",
      };
    }
    return {
      backgroundColor: isDark ? "#0a0a0a" : "#ffffff",
      color: isDark ? "#ffffff" : "#1a1a1a",
      boxShadow: isDark
        ? "4px 4px 8px #000000, -4px -4px 8px #141414"
        : "4px 4px 8px #d1d1d1, -4px -4px 8px #ffffff",
    };
  };

  if (showSquareFootageOnly) {
    // User has permit or is working with architect - show square footage input
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 
            className="text-2xl md:text-3xl font-bold mb-2"
            style={{ color: "#E4683B" }}
          >
            What size ADU are you planning?
          </h2>
          <p 
            className="text-lg opacity-80"
            style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
          >
            Enter the square footage from your plans
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="space-y-2">
            <Label htmlFor="squareFootage" className={labelStyles}>
              <Ruler className="w-4 h-4 inline mr-2" style={{ color: "#E4683B" }} />
              Square Footage *
            </Label>
            <div className="relative">
              <Input
                id="squareFootage"
                type="number"
                value={formData.squareFootage}
                onChange={(e) => updateFormData({ squareFootage: e.target.value })}
                placeholder="e.g., 750"
                className={inputStyles}
                min="200"
                max="1200"
              />
              <span 
                className="absolute right-4 top-1/2 -translate-y-1/2 text-lg opacity-60"
                style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
              >
                sq ft
              </span>
            </div>
            <p 
              className="text-sm opacity-60"
              style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
            >
              ADUs in California can be up to 1,200 square feet
            </p>
          </div>
        </div>
      </div>
    );
  }

  // User doesn't have plans yet - show finish material options
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-2"
          style={{ color: "#E4683B" }}
        >
          Choose your finish level
        </h2>
        <p 
          className="text-lg opacity-80"
          style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
        >
          This helps us estimate your investment
        </p>
      </div>

      <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
        {FINISH_LEVELS.map((level) => {
          const isSelected = formData.finishLevel === level.value;
          
          return (
            <button
              key={level.value}
              onClick={() => updateFormData({ finishLevel: level.value })}
              className="p-6 rounded-2xl text-center transition-all duration-300 hover:scale-[1.02]"
              style={getCardStyles(isSelected)}
            >
              <div 
                className="flex justify-center mb-4"
                style={{ color: isSelected ? "#ffffff" : "#E4683B" }}
              >
                {iconMap[level.value]}
              </div>
              <h3 className="font-bold text-xl mb-2">{level.label}</h3>
              <p 
                className="text-sm mb-3"
                style={{ opacity: isSelected ? 0.9 : 0.7 }}
              >
                {level.description}
              </p>
              <p 
                className="text-lg font-semibold"
                style={{ color: isSelected ? "#ffffff" : "#E4683B" }}
              >
                +${level.cost}/sq ft
              </p>
            </button>
          );
        })}
      </div>

      {/* Optional square footage input */}
      <div className="max-w-md mx-auto mt-8">
        <div className="space-y-2">
          <Label htmlFor="squareFootage" className={labelStyles}>
            <Ruler className="w-4 h-4 inline mr-2" style={{ color: "#E4683B" }} />
            Desired Square Footage (optional)
          </Label>
          <div className="relative">
            <Input
              id="squareFootage"
              type="number"
              value={formData.squareFootage}
              onChange={(e) => updateFormData({ squareFootage: e.target.value })}
              placeholder="e.g., 750"
              className={inputStyles.replace("py-6", "py-3")}
              min="200"
              max="1200"
            />
            <span 
              className="absolute right-4 top-1/2 -translate-y-1/2 opacity-60"
              style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
            >
              sq ft
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step6SizeMaterials;
